import type { Rule } from '@/plugins/casl/ability'

let abilitiesCache: Rule[] | null = null
let fetchPromise: Promise<Rule[]> | null = null

/**
 * Busca as abilities (permissões CASL) do usuário autenticado via endpoint dedicado.
 * Usa cache em memória para evitar chamadas duplicadas durante a mesma sessão.
 */
export async function fetchAbilities(): Promise<Rule[]> {
  if (abilitiesCache) return abilitiesCache

  if (fetchPromise) return fetchPromise

  fetchPromise = (async () => {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'

      const response = await fetch(`${baseUrl}/oauth2/abilities`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        abilitiesCache = []
        return []
      }

      const data = await response.json()

      const rules: Rule[] = []

      if (data && typeof data === 'object') {
        for (const [key, actions] of Object.entries(data)) {
          if (Array.isArray(actions)) {
            actions.forEach((action: string) => {
              rules.push({
                action: action.toString(),
                subject: key.toString(),
              })
            })
          }
        }
      }

      abilitiesCache = rules
      return rules
    } catch {
      abilitiesCache = []
      return []
    } finally {
      fetchPromise = null
    }
  })()

  return fetchPromise
}

/**
 * Limpa o cache de abilities (usar no logout ou troca de perfil).
 */
export function clearAbilitiesCache() {
  abilitiesCache = null
  fetchPromise = null
}

/**
 * Força recarregar as abilities do servidor.
 */
export async function refreshAbilities(): Promise<Rule[]> {
  clearAbilitiesCache()
  return fetchAbilities()
}
